import React, { useEffect, useRef, useState } from 'react';
import { HiOutlinePlus, HiOutlineChevronDown } from 'react-icons/hi2';

import { STEP_KIND, STEP_KIND_META } from '../model/playbook-model';

/**
 * The "+ Add step" affordance. Lists the kinds with their verb and a one-line
 * explanation, so an author picks by intent rather than by jargon.
 */

// IF is offered only where nesting is allowed - a branch body cannot itself
// branch in this first cut, which keeps the compiled `conditions` one level deep.
const ORDER = [
    STEP_KIND.SEND,
    STEP_KIND.RUN,
    STEP_KIND.ASK,
    STEP_KIND.SET,
    STEP_KIND.IF,
    STEP_KIND.GO_TO,
    STEP_KIND.HANDOFF,
    STEP_KIND.RUN_PLAYBOOK,
];

export default function AddStepMenu({ onAdd, allowIf = true, label = 'Add step', trigger, ariaLabel = 'Choose step type' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const kinds = ORDER.filter(k => allowIf || k !== STEP_KIND.IF);

    return (
        <div className="relative flex items-center gap-1" ref={ref} onKeyDown={e => {
            if (e.key === 'Escape' && open) {
                e.stopPropagation();
                setOpen(false);
                ref.current?.querySelector('[aria-expanded]')?.focus();
            }
        }}>
            {!trigger && (
            <button
                type="button"
                onClick={() => onAdd(STEP_KIND.SEND)}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            >
                <HiOutlinePlus size={14} /> {label}
            </button>

            )}

            <button type="button" aria-label={ariaLabel} aria-expanded={open}
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                onMouseDown={e => e.stopPropagation()}
                className={trigger
                    ? 'rounded-md transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary/20'
                    : 'rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus:ring-2 focus:ring-primary/20'}>
                {trigger || <HiOutlineChevronDown size={14} />}
            </button>

            {open && (
                <div className="absolute left-0 top-full z-20 mt-1 w-72 overflow-hidden rounded-xl border border-gray-200 bg-white p-1 shadow-lg ring-1 ring-black/[0.03]">
                    {kinds.map(kind => {
                        const meta = STEP_KIND_META[kind];
                        return (
                            <button
                                key={kind}
                                type="button"
                                onClick={e => { e.stopPropagation(); onAdd(kind); setOpen(false); }}
                                className="flex w-full items-start gap-2.5 px-2.5 py-2 text-left transition-colors hover:bg-gray-50"
                            >
                                <span
                                    className={`mt-px shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider
                                        ${meta.verbClass} ${meta.verbBg}`}
                                >
                                    {meta.verb}
                                </span>
                                <span className="text-[12px] leading-snug text-gray-500">{meta.tip}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
